/**
 * useClickOutside — shared hook for click-outside + Escape key dismissal.
 *
 * Listens for pointerdown on document when `active=true`. Fires `onClose`
 * when the click target falls outside ALL provided refs.  Uses a setTimeout(0)
 * guard to prevent the opening click from immediately closing the popover
 * (same pattern as SoundConfigFlyover).
 *
 * Also handles Escape key with stopPropagation so the key doesn't bubble
 * to ReactFlow's default canvas-deselect handler.
 */

import { useEffect, useRef } from 'react'

export function useClickOutside(
  refs: React.RefObject<HTMLElement | null>[],
  onClose: () => void,
  active: boolean,
): void {
  // Store refs and onClose in a mutable ref so the effect doesn't
  // re-run when the caller passes a new array or callback identity.
  const stableRefs = useRef(refs)
  stableRefs.current = refs
  const stableOnClose = useRef(onClose)
  stableOnClose.current = onClose

  useEffect(() => {
    if (!active) return

    let listening = false

    const handlePointerDown = (e: PointerEvent) => {
      if (!listening) return
      if (!(e.target instanceof HTMLElement)) return
      const target = e.target
      const isInside = stableRefs.current.some(
        (ref) => ref.current && ref.current.contains(target),
      )
      if (!isInside) {
        stableOnClose.current()
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        stableOnClose.current()
      }
    }

    // Delay listener registration by one tick so the opening click
    // (which sets active=true) doesn't immediately fire onClose.
    const timer = setTimeout(() => {
      listening = true
    }, 0)

    // Register pointerdown in capture phase so the document handler sees the
    // event before any descendant `stopPropagation` (e.g. UnifiedBottomPanel)
    // can swallow it. This lets clicks inside the bottom panel still close
    // open config popouts.
    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [active])
}
